import { parseIntDataAttr } from "@ui-utils/Injector/utils";
import { tick } from "svelte";

type UpdateFunction = (newTarget: HTMLElement | string) => Promise<void>;

type PortalReturnType = {
    update: UpdateFunction;
    destroy: () => void;
};

export type PortalParams = {
    target?: HTMLElement | string;
    order?: number;
    disablePortal?: boolean;
};

/**
 * Usage: <div use:portal={{ target: 'css selector', order?: number }}> or <div use:portal={{ target: document.body, order?: number }}>
 */
export function portal(
    el: HTMLElement,
    params?: PortalParams
): PortalReturnType | void {
    let target = params?.target || "body";
    const order = params?.order;
    const disablePortal = params?.disablePortal;

    if (disablePortal) return;

    let targetEl;
    async function update(newTarget: HTMLElement | string): Promise<void> {
        target = newTarget;
        if (typeof target === "string") {
            targetEl = document.querySelector<HTMLElement>(target);
            if (targetEl === null) {
                await tick();
                targetEl = document.querySelector<HTMLElement>(target);
            }
            if (targetEl === null) {
                throw new Error(
                    `No element found matching css selector: "${target}"`
                );
            }
        } else if (target instanceof HTMLElement) {
            targetEl = target;
        } else {
            throw new TypeError(
                `Unknown portal target type: ${
                    target === null ? "null" : typeof target
                }. Allowed types: string (CSS selector) or HTMLElement.`
            );
        }

        insertElement(targetEl, el, order);

        el.hidden = false;
    }

    function insertElement(
        target: HTMLElement,
        element: HTMLElement,
        order?: number
    ): void {
        if (!order) {
            target.appendChild(el);
            return;
        }

        const items = Array.from(
            target.querySelectorAll<HTMLElement>("[data-order]")
        );

        element.setAttribute("data-order", order.toString());

        const nextElement = items.find(
            (item) => order < (parseIntDataAttr(item, "order") || 0)
        );

        if (nextElement) {
            target.insertBefore(element, nextElement);
        } else {
            target.appendChild(element);
        }
    }

    function destroy() {
        if (el.parentNode) {
            el.parentNode.removeChild(el);
        }
    }
    void update(target);
    return {
        update,
        destroy,
    };
}
